import type { BackTestActions } from 'state/backTest/backTest.actions';
import type { BlottersAction } from 'state/blotters/blotter.actions';
import { CommonFormAction } from 'state/forms/actions/form';
import { CommonQuoteAction } from 'state/forms/actions/quote';
import { CommonStreamingAction } from 'state/forms/actions/stream';
import { CommonTradeAction } from 'state/forms/actions/trade';
import type { HelpCenterActions } from 'state/helpRequest/actions';
import type { BdrMonthlyExpiryCalendarsActions } from 'state/monthlyExpiryDates/monthlyExpiryDates.action';
import type { CorrelationActions } from 'state/quotes/correlations/correlations.action';
import type { SelectedCorrelationGraphActions } from 'state/quotes/selectedCorrelationGraph/selectedCorrelationGraph.action';
import type { ScheduledRunActions } from 'state/scheduledRuns/actions';
import type { UnderlyingHistoActions } from 'state/underlyingHistograph/underlyingHisto.action';
import { UnderlyingsDatesActions } from 'state/underlyingsDates/underlyingsDates.action';
import type { UserAction } from 'state/user/user.action';
import { SignalrConnectionEventActions } from 'utils/streaming/actions';

import type { AddonFormAction } from './addonForm/addonForm.action';
import type { AddonManagementFormActions } from './addonManagementForm/addonManagementForm.action';
import type { SgAdvisoryAction } from './advisory/actions';
import type { BasketOptimizerAction } from './advisory/basket-optimizer/actions';
import type { AppCrashedActions } from './appCrashed/appCrash.actions';
import type { BatchFormAction } from './batchForm/batchForm.action';
import type { DailyBlotterActions } from './blotters/dailyBlotter/dailyBlotter.action';
import type { DiscoverAction } from './discover/discover.actions';
import type { DocumentDownloadActions } from './document/actions';
import type { DynamicFormAction } from './dynamic/dynamicForm.action';
import type { EmailServiceActions } from './emailService/emailService.actions';
import type { FormDataAction } from './formData/formData.action';
import type { MissedDealFeedbackAction } from './missed-deal-feeedback/actions';
import type { SessionNotificationsAction } from './notification';
import type { UserNotificationActions } from './notifications/notification.action';
import { OrdersBasketFormsActions } from './orders-basket/actions';
import type { ProductDescriptionAction } from './product-description/action';
import type { ProductModificationFormAction } from './productModificationForm/productModificationForm.action';
import type { ProductsReferentialAction } from './productsReferential/productsReferential.action';
import type { ProductSummaryActions } from './productSummaryDescription/productSummaryDescription.action';
import type { BookingActions } from './quotes/booking/booking.actions';
import type { PriceImprovementRequestActions } from './quotes/priceEnhancements/priceImprovementRequest/priceImprovementRequest.actions';
import type { PriceImprovementResponseActions } from './quotes/priceEnhancements/priceImprovementResponse/priceImprovementResponse.actions';
import type { QuotesAction } from './quotes/quotes.actions';
import type { QuoteTradeActions } from './quotes/trades/trades.action';
import type { RouterAction } from './router';
import type { SecondaryMarketActions } from './secondaryMarket';
import type { BdrSettlementCalendarsActions } from './settlementDateCalendars/settlementDateCalendars.action';
import type { TradeAllocationFormAction } from './tradeAllocationForm/tradeAllocationForm.actions';
import type { TradesActions } from './trades/trades.actions';
import type { UiPanelAction } from './uiPanelState/actions';
import type { UserConsentActions } from './userConsent/userConsent.actions';
import type { UserPreferencesAction } from './userPreferences/userPreferences.action';
import type { BdrValuationCalendarsActions } from './valuationDateCalendars/valuationDateCalendars.action';
import type { OrdersBasketActions } from './workspaces/ordersBasket/ordersBasket.actions';
import type { WorkspaceActions } from './workspaces/workspace.actions';

export type Action =
  | AppCrashedActions
  | AddonFormAction
  | AddonManagementFormActions
  | BasketOptimizerAction
  | BdrMonthlyExpiryCalendarsActions
  | BdrSettlementCalendarsActions
  | BdrValuationCalendarsActions
  | BlottersAction
  | CommonFormAction
  | CommonQuoteAction
  | CommonStreamingAction
  | CommonTradeAction
  | DailyBlotterActions
  | DynamicFormAction
  | FormDataAction
  | MissedDealFeedbackAction
  | SessionNotificationsAction
  | OrdersBasketActions
  | OrdersBasketFormsActions
  | ProductDescriptionAction
  | ProductModificationFormAction
  | ProductSummaryActions
  | ProductsReferentialAction
  | QuotesAction
  | BookingActions
  | QuoteTradeActions
  | CorrelationActions
  | SelectedCorrelationGraphActions
  | RouterAction
  | SgAdvisoryAction
  | BatchFormAction
  | UnhandledError
  | UserAction
  | UserConsentActions
  | UiPanelAction
  | WorkspaceActions
  | DocumentDownloadActions
  | TradesActions
  | UserPreferencesAction
  | TradeAllocationFormAction
  | UserNotificationActions
  | DiscoverAction
  | PriceImprovementRequestActions
  | PriceImprovementResponseActions
  | SecondaryMarketActions
  | EmailServiceActions
  | UnderlyingHistoActions
  | BackTestActions
  | ScheduledRunActions
  | HelpCenterActions
  | UnderlyingsDatesActions
  | SignalrConnectionEventActions;

export function unhandledError(message: string, error: Record<string, unknown>, epicName: string = 'unknown epic') {
  return {
    type: 'UNHANDLED_ERROR' as const,
    message,
    error,
    epicName,
  };
}
export type UnhandledError = ReturnType<typeof unhandledError>;
